import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Plus, Upload, ChevronRight, Trash2 } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { listsAPI } from '../services/api.js';
import { formatDateTime } from '../lib/format.js';

export default function Lists() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await listsAPI.list();
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load lists.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) return;
    try {
      const r = await listsAPI.create({
        name: draft.name.trim(),
        description: draft.description.trim() || undefined
      });
      setCreating(false);
      setDraft({ name: '', description: '' });
      navigate(`/lists/${r.data.id}`);
    } catch (err) {
      setError(err.message || 'Create failed.');
    }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete the list "${name}" and all its members?`)) return;
    try {
      await listsAPI.remove(id);
      setItems((list) => list.filter((l) => l.id !== id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl">
      <PageHeader
        title="Contact lists"
        subtitle="Reusable databases. Build a list once; any campaign can dial it."
        actions={
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            <Plus size={14} /> New list
          </button>
        }
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}

      {creating && (
        <div className="card mb-6 border-alfred-gold/30">
          <h2 className="text-stone-100 font-medium mb-3">New list</h2>
          <div className="space-y-3">
            <input
              className="field-input"
              placeholder="List name (e.g. Houston HVAC)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              autoFocus
            />
            <input
              className="field-input"
              placeholder="Description (optional)"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
            <div className="flex gap-2">
              <button type="button" className="btn-primary" onClick={create}>Create</button>
              <button type="button" className="btn-ghost" onClick={() => { setCreating(false); setDraft({ name: '', description: '' }); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Members</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium" />
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-stone-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-stone-500">
                No lists yet. Click <em>New list</em>, then upload a CSV. Campaigns can pull from any list you create.
              </td></tr>
            ) : items.map((l) => (
              <tr
                key={l.id}
                onClick={() => navigate(`/lists/${l.id}`)}
                className="border-b border-stone-900 hover:bg-stone-900/50 cursor-pointer transition"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Database size={14} className="text-alfred-gold" />
                    <span className="text-stone-100 font-medium">{l.name}</span>
                  </div>
                  {l.description && (
                    <div className="text-xs text-stone-500 mt-0.5 truncate max-w-md">{l.description}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-stone-300 tabular-nums">{l.memberCount}</td>
                <td className="px-4 py-3 text-stone-400 whitespace-nowrap">{formatDateTime(l.createdAt)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); remove(l.id, l.name); }}
                    className="text-red-300 hover:text-red-200 p-1"
                    aria-label="Delete list"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
                <td className="px-4 py-3 text-stone-500"><ChevronRight size={16} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
